import React, { useEffect, useState } from "react";
import { QrCode, Smartphone } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "../hooks/use-toast";
import { adminApi } from "../lib/api";

const EMPTY = { upiId: "", upiHolder: "", qrMerchant: "", qrImage: "" };

// Admin: UPI-ID + QR details shown on the manual deposit tab (bank account has its own editor).
export default function AdminManualPayMethods({ token }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.getManualPayMethods(token).then((r) => setForm({ ...EMPTY, ...(r || {}) })).catch(() => {}); }, [token]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 400 * 1024) { toast({ title: "QR image too large", description: "Max 400 KB — crop or compress it." }); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, qrImage: String(reader.result) }));
    reader.readAsDataURL(f);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.setManualPayMethods(token, form);
      setForm({ ...EMPTY, ...r });
      toast({ title: "Manual deposit methods saved ✅", description: "Users now see the UPI ID / QR on the Manual Deposit tab." });
    } catch (e) {
      toast({ title: "Could not save", description: e?.response?.data?.detail || "Try again" });
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 max-w-lg space-y-4" data-testid="admin-manual-methods-editor">
      <h3 className="font-display font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /> Manual Deposit · UPI ID & QR</h3>
      <p className="text-xs text-muted-foreground">Shown read-only on the Manual Deposit tab (UPI Payment / Payment QR Code). Leave blank to hide a method.</p>
      <div><Label>UPI ID</Label><Input value={form.upiId} onChange={(e) => setForm({ ...form, upiId: e.target.value.trim() })} placeholder="e.g. batball@okaxis" className="mt-1.5" data-testid="admin-manual-upi-id" /></div>
      <div><Label>UPI holder name</Label><Input value={form.upiHolder} onChange={(e) => setForm({ ...form, upiHolder: e.target.value })} placeholder="e.g. BatBallWorld Payments" className="mt-1.5" data-testid="admin-manual-upi-holder" /></div>
      <div><Label>QR merchant name</Label><Input value={form.qrMerchant} onChange={(e) => setForm({ ...form, qrMerchant: e.target.value })} placeholder="e.g. BatBallWorld" className="mt-1.5" data-testid="admin-manual-qr-merchant" /></div>
      <div>
        <Label className="flex items-center gap-1.5"><QrCode className="h-3.5 w-3.5" /> QR code image</Label>
        <input type="file" accept="image/*" onChange={onFile} className="mt-1.5 block text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-black" data-testid="admin-manual-qr-file" />
        {form.qrImage && (
          <div className="mt-2 flex items-center gap-3">
            <img src={form.qrImage} alt="QR preview" className="h-24 w-24 rounded-md bg-white p-1 object-contain" data-testid="admin-manual-qr-preview" />
            <Button size="sm" variant="outline" className="bg-white/5 border-white/15" onClick={() => setForm({ ...form, qrImage: "" })} data-testid="admin-manual-qr-remove">Remove</Button>
          </div>
        )}
      </div>
      <Button className="font-semibold" onClick={save} disabled={saving} data-testid="admin-manual-methods-save">{saving ? "Saving…" : "Save UPI / QR Details"}</Button>
    </div>
  );
}
